import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { GenericNavigationPanelProps, SubNavigation } from "../SubNavigation";
import {
	EMPTY_ARRAY,
	isArray,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { shallowComparison, deepComparison } from "client/Misc/compareUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ActionKind,
	Action,
	ActionGroup
} from "client_legacy/ndaw/actions";
import { useShouldStartLoadingData } from "./useShouldStartLoadingData";
import {
	AlertManager,
	useAlerts,
	useForceRerender,
	useStableImperativeCallback,
	useSynchronizedRef,
	useUserTimezone,
} from "client/Hooks";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	GlobalNavigationItemKind,
	GlobalNavigationItemSaveModel,
	GlobalNavigationItemViewModelKind,
	GlobalNavigationPermissionsModel,
	GlobalNavigationViewModel,
	IGlobalNavigationItemDataModel,
	addNavigationItems,
	getNavigation,
	GlobalNavigationItemModel,
	GroupGlobalNavigationItemDataModel,
	GlobalNavigationSaveResult
} from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import {
	BusyIndicator,
	Icon,
	TreeView,
	TreeViewHandle,
	TreeViewItem
} from "client/Components";
import { GlobalNavigationBarMode, GlobalNavigationItem } from "client_legacy/TitleBar/GlobalNavigation/navigationItem";
import { GlobalNavigationCriteria, useGlobalNavigationStore } from "../../TitleBar/GlobalNavigation/globalNavigationStore";
import { useAddAppsAsTabsSidebar } from "../../TitleBar/GlobalNavigation/AddAppsAsTabs/AddAppsAsTabs";
import {
	GlobalNavigationManagementInitialAction,
	useGlobalNavigationManagementSidebar
} from "../../TitleBar/GlobalNavigation/GlobalNavigationManagement/GlobalNavigationManagement";
import { AppType } from "client/Api/Apps/common";
import { useHandleNavigationInGlobalSubNavigation } from "../useGlobalNavigationVisibilityState";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SpaceNavigationItem } from "client_legacy/TitleBar/GlobalNavigation/spaceNavigationItem";
import { AppNavigationItem, DisabledStatus } from "client_legacy/TitleBar/GlobalNavigation/appNavigationItem";
import { never } from "client_legacy/ndaw/errors";
import { TimeZone } from "client/DateAndTime/timezones";
import styles from "./SpacePanel.module.less";
import { ISpaceNavigationHandler, useSpaceNavigationHandler } from "./useSpaceNavigationHandler";
import { useDeleteConfirmationDialog } from "../../TitleBar/GlobalNavigation/GlobalNavigationManagement/DeleteConfirmationDialog";
import {
	deleteSpace,
	SpaceAccessKind,
	SpaceInfo,
	SpaceKind
} from "client/Api/Workspace/workspace";
import { useWorkspaceControllerStore, WorkspaceController } from "client/Features/Workspace/useWorkspaceControllerStore";
import { SavedSpace, useEditSpaceDialog } from "client/Features/Workspace/EditSpaceDialog/EditSpaceDialog";
import { useStartCreateSpace } from "client/Features/Workspace/useStartCreateSpace";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { registerWorkspaceSubscriptions } from "client/Features/Workspace/Workspace";
import {
	WorkspaceEvent,
	publish as workspacePublish
} from "client/Features/Workspace/events";

type SpacePanelProps = GenericNavigationPanelProps;

export const SpacePanel = (props: SpacePanelProps): ReactElement => {
	const {
		isVisible,
		isPinned,
		isActive
	} = props;

	const [data, setData] = useState<GlobalNavigationViewModel>();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const handleNavigationInGlobalSubNavigation: () => void = useHandleNavigationInGlobalSubNavigation();
	const workspaceController: OptionalModel<WorkspaceController> = useWorkspaceControllerStore(x => x.controller);
	const workspaceControllerRef: ReadonlyRefObject<OptionalModel<WorkspaceController>> = useSynchronizedRef(workspaceController);

	const {
		activeItemCriteria,
		mode,
		workspaceSpaceNavigationHandlerOptions
	} = useGlobalNavigationStore(
		x => ({
			activeItemCriteria: x.activeCriteria,
			mode: x.mode,
			workspaceSpaceNavigationHandlerOptions: x.workspaceSpaceNavigationHandlerOptions
		}),
		shallowComparison
	);

	const [addAppsAsTabsSidebar, openAddAppsAsTabsSidebar] = useAddAppsAsTabsSidebar();
	const [managementSidebar, openManagementSidebar] = useGlobalNavigationManagementSidebar();
	const [editSpaceDialog, openEditSpaceDialog] = useEditSpaceDialog();
	const [deleteConfirmationDialogElement, openDeleteConfirmationDialog] = useDeleteConfirmationDialog();
	const [startCreateSpaceElement, startCreateSpace] = useStartCreateSpace();

	const shouldStartLoadingData: boolean = useShouldStartLoadingData(isPinned, isVisible);
	const [reload, dataVersion] = useForceRerender();

	const alertManager: AlertManager = useAlerts();
	const timezone: TimeZone = useUserTimezone();

	const actionsAvailability: GlobalNavigationActionsAvailability = useGlobalNavigationActionsAvailability(
		data?.permissions ?? initialPermission
	);

	const [treeViewHandle, setTreeViewHandle] = useState<TreeViewHandle<GlobalNavigationItem> | null>(null);
	const shouldExpandAllGroupsRef: RefObject<boolean> = useRef<boolean>(false);

	const [spaceNavigationElement, spaceNavigationHandler] = useSpaceNavigationHandler(workspaceSpaceNavigationHandlerOptions);
	const itemToActivateAfterLoadRef: RefObject<GlobalNavigationCriteria | undefined> = useRef<GlobalNavigationCriteria | undefined>(undefined);
	const activeItemRef: RefObject<GlobalNavigationItem | undefined> = useRef<GlobalNavigationItem>(undefined);
	const modeRef: ReadonlyRefObject<GlobalNavigationBarMode> = useSynchronizedRef(mode);

	const canDeactivateActiveItem = useCallback(
		async (): Promise<boolean> => {
			// NOTE: this function needs to be entirely stable and utilizes a ref for the active item:
			// - this function depends on the active item
			// - active item depends on items
			// - items depends on item actions
			// - item actions depend on this function
			const { current: activeItem } = activeItemRef;

			if (isNil(activeItem)) {
				return true;
			}

			return await activeItem.canDeactivate(modeRef.current);
		},
		[modeRef]
	);

	const editSpaceHandler = useCallback(
		async (spaceId: number): Promise<void> => {
			if (!await canDeactivateActiveItem()) {
				return;
			}

			const result: TryResult<SavedSpace> = await openEditSpaceDialog({
				mode: {
					kind: "edit",
					id: spaceId
				}
			 });

			if (!result.wasSuccessful) {
				return;
			}

			const { result: space } = result;

			if (space.kind !== SpaceKind.Shared) {
				reload();
				return;
			}

			const {
				id,
				kind,
				name,
				userInfo
			} = space;

			const updatedSpace: SpaceInfo = {
				id,
				kind,
				name,
				accessKind: userInfo.canEdit
					? SpaceAccessKind.Edit
					: SpaceAccessKind.Access
			};

			workspacePublish(
				WorkspaceEvent.SpaceUpdated,
				{
					space: updatedSpace
				}
			);
		},
		[
			canDeactivateActiveItem,
			openEditSpaceDialog,
			reload
		]
	);

	const deleteSpaceHandler = useCallback(
		async (spaceId: number, spaceName: string): Promise<void> => {
			if (!await canDeactivateActiveItem()) {
				return;
			}

			if (!await openDeleteConfirmationDialog({ name: spaceName })) {
				return;
			}

			try {
				setIsBusy(true);
				await deleteSpace(spaceId);

				workspacePublish(
					WorkspaceEvent.SpaceDeleted,
					{
						spaceId
					}
				);
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			canDeactivateActiveItem,
			openDeleteConfirmationDialog
		]
	);

	const itemActions = useMemo(
		(): (readonly ItemAction[]) | undefined => {
			if (isActive) {
				return listSelectionActions({
					onEditSpace: editSpaceHandler,
					onDeleteSpace: deleteSpaceHandler
				});
			}

			return undefined;
		},
		[
			deleteSpaceHandler,
			editSpaceHandler,
			isActive
		]
	);

	const items = useMemo(
		(): TreeViewItem<GlobalNavigationItem>[] => {
			if (!data) {
				return [];
			}

			return data.items
				.filter(
					(x) => isGroupItem(x.itemData)
						? x.itemData.children.length > 0
						: true
				)
				.map(
					(x) => convertToGlobalNavigationItem(
						x,
						spaceNavigationHandler,
						timezone
					)
				)
				.map(
					(x): TreeViewItem<GlobalNavigationItem> => convertToTreeItem(
						x,
						mode,
						itemActions
					)
				);
		},
		[
			data,
			itemActions,
			mode,
			spaceNavigationHandler,
			timezone
		]
	);

	useEffect(
		(): void => {
			if (!treeViewHandle || !shouldExpandAllGroupsRef.current) {
				return;
			}

			const groups: GlobalNavigationItem[] = items.map(i => i.value)
				.filter(i => i instanceof GroupNavigationItem);

			treeViewHandle.expandNodes(groups);

			shouldExpandAllGroupsRef.current = false;
		},
		[items, treeViewHandle]
	);

	const activeItem = useMemo(
		(): GlobalNavigationItem | undefined => {
			if (isNotNil(activeItemCriteria)) {
				return items
					.reduce(
						(
							acc: TreeViewItem<GlobalNavigationItem>[],
							current: TreeViewItem<GlobalNavigationItem>
						): TreeViewItem<GlobalNavigationItem>[] => {
							return [
								...acc,
								current,
								...(Array.isArray(current.children) ? current.children : EMPTY_ARRAY)
							];
						},
						[]
					)
					.map(i => i.value)
					.find(x => deepComparison(activeItemCriteria, convertToCriteria(x)));
			}

			return undefined;
		},
		[activeItemCriteria, items]
	);
	activeItemRef.current = activeItem;

	const activateItemHandler = useCallback(
		async (item: GlobalNavigationItem): Promise<void> => {
			handleNavigationInGlobalSubNavigation();

			if (isNotNil(activeItem)) {
				if (activeItem.id === item.id) {
					return;
				}

				if (!(await activeItem.canDeactivate(mode))) {
					return;
				}
			}

			item.activate(mode);
		},
		[
			activeItem,
			handleNavigationInGlobalSubNavigation,
			mode
		]
	);

	const addAppAsTabHandler = useCallback(
		async (): Promise<void> => {
			if (!await canDeactivateActiveItem()) {
				return;
			}

			openAddAppsAsTabsSidebar(
				{
					async onAdd(selectedApps): Promise<void> {
						const newItems: GlobalNavigationItemSaveModel[] = selectedApps.map(
							newApp => ({
								kind: GlobalNavigationItemKind.InstalledApp,
								id: null,
								appData: {
									installedAppId: newApp.installedAppId
								}
							})
						);

						const result: GlobalNavigationSaveResult = await addNavigationItems({ newItems });

						if (result.addedNavigationItems.length > 0) {
							const { itemData } = result.addedNavigationItems[0];

							let criteria: GlobalNavigationCriteria | undefined;

							if (itemData.kind === GlobalNavigationItemViewModelKind.GenericApp
								&& itemData.appType === AppType.Embed
							) {
								criteria = {
									type: "embedApp",
									installedAppId: itemData.installedAppId
								};
							}

							if (isNil(criteria)) {
								console.error(`Unexpected added navigation item '${JSON.stringify(itemData)}'`);
							} else {
								if (workspaceController.isSet) {
									workspaceController.value
										.cancelEditMode();
								}

								itemToActivateAfterLoadRef.current = criteria;
							}
						}

						reload();
					}
				}
			);
		},
		[
			canDeactivateActiveItem,
			openAddAppsAsTabsSidebar,
			reload,
			workspaceController
		]
	);

	const manageHandler = useCallback(
		async (initialAction?: GlobalNavigationManagementInitialAction): Promise<void> => {
			if (!await canDeactivateActiveItem()) {
				return;
			}

			const didChange: boolean = await openManagementSidebar({ initialAction });

			if (didChange) {
				reload();

				const { current: controller } = workspaceControllerRef;
				if (controller?.isSet) {
					controller.value
						.refresh();
				}
			}
		},
		[
			workspaceControllerRef,
			canDeactivateActiveItem,
			openManagementSidebar,
			reload
		]
	);

	const headerActions = useMemo(
		(): readonly Action<GlobalNavigationActionsAvailability>[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "create-shared-space",
					icon: "rocket",
					title: localize("_CreateSharedSpace"),
					canExecute: (c): boolean => c.isCreateSharedSpaceAvailable,
					execute: () => startCreateSpace(),
					group: createGroup
				},
				{
					kind: ActionKind.Regular,
					name: "add-app-as-tab",
					icon: "code",
					title: localize("_AddWebContentAsSpace"),
					canExecute: (c): boolean => c.isAddAppAsTabAvailable,
					execute: addAppAsTabHandler,
					group: createGroup
				},
				{
					kind: ActionKind.Regular,
					name: "create-group",
					icon: "layer-group",
					title: localize("_CreateGroup"),
					canExecute: (c): boolean => c.isCreateSharedSpaceAvailable,
					execute: () => manageHandler("create-group"),
					group: createGroup
				},
				{
					kind: ActionKind.Regular,
					name: "manage",
					icon: "gear",
					title: localize("_Manage"),
					canExecute: (c): boolean => c.isManageAvailable,
					execute: () => manageHandler()
				}

			];
		},
		[
			addAppAsTabHandler,
			startCreateSpace,
			manageHandler
		]
	);

	const activateItemByCriteria = useStableImperativeCallback(
		async (criteria: GlobalNavigationCriteria): Promise<void> => {
			const itemResult: TreeViewItem<GlobalNavigationItem>[] | undefined = getPathToItem(items, criteria);

			if (!itemResult) {
				return;
			}

			if (!await canDeactivateActiveItem()) {
				return;
			}

			const parents: readonly GlobalNavigationItem[] = itemResult.slice(0, -1)
				.map((x) => x.value);

			const item: GlobalNavigationItem = itemResult[itemResult.length - 1]
				.value;

			if (!item.canActivate(mode)) {
				return;
			}

			if (treeViewHandle) {
				if (parents.length > 0) {
					treeViewHandle.expandNodes(parents);
				}

				treeViewHandle.scrollIntoView(item);
			}

			item.activate(mode);
		}
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(dataVersion);
			if (!shouldStartLoadingData) {
				return;
			}

			async function fetchItems(signal: AbortSignal): Promise<void> {
				setIsLoading(true);
				try {
					const navigation: GlobalNavigationViewModel = await getNavigation(signal);
					setData(navigation);
					setIsLoading(false);

					const { current: itemToActivate } = itemToActivateAfterLoadRef;
					if (itemToActivate) {
						// Use setTimeout as the data needs to be converted to items, the TreeView
						// needs to be rendered (ref set)
						setTimeout(
							(): void => {
								activateItemByCriteria(itemToActivate);

								const { current: controller } = workspaceControllerRef;

								if (itemToActivate.type === "space"
									&& controller.isSet
								) {
									controller.value
										.refresh();
								}
							},
							1
						);

						itemToActivateAfterLoadRef.current = undefined;
					}

					shouldExpandAllGroupsRef.current = true;
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();

			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			activateItemByCriteria,
			alertManager,
			dataVersion,
			shouldStartLoadingData,
			workspaceControllerRef
		]
	);

	useEffect(
		(): () => void => {
			const subscriptionTokens: SubscriptionTokenCollection = registerWorkspaceSubscriptions({
				spaceAdded: reload,
				spaceUpdated: reload,
				spaceDeleted: reload
			});

			return (): void => subscriptionTokens.destroy();
		},
		[reload]
	);

	const renderContent = (): ReactElement => {
		if (isLoading || !shouldStartLoadingData) {
			return <SubNavigation.LoadingIndicator />;
		}

		return (
			<TreeView
				value={activeItem}
				onChange={activateItemHandler}
				compareBy="id"
				items={items}
				trackingId="space-panel-tree-view"
				ref={setTreeViewHandle}
			/>
		);
	};

	return (
		<>
			<SubNavigation.Header
				kind="space"
				title={localize("_Spaces")}
				actions={isActive ? headerActions : undefined}
				actionContext={actionsAvailability}
			/>
			<SubNavigation.Body>
				{isBusy && (
					<BusyIndicator />
				)}

				{renderContent()}
			</SubNavigation.Body>

			{addAppsAsTabsSidebar}
			{managementSidebar}
			{spaceNavigationElement}
			{deleteConfirmationDialogElement}
			{editSpaceDialog}
			{startCreateSpaceElement}
		</>
	);
};

SpacePanel.displayName="SpacePanel";

const initialPermission: GlobalNavigationPermissionsModel = {
	canChangeGlobalNavigationOrder: false,
	canManageGlobalNavigation: false,
	canManageSharedSpaces: false,
	canManageSpacePermissions: false
};

function isGroupItem(item: IGlobalNavigationItemDataModel): item is GroupGlobalNavigationItemDataModel {
	return item.kind === GlobalNavigationItemViewModelKind.Group;
}

type GlobalNavigationActionsAvailability = {
	isCreateSharedSpaceAvailable: boolean;
	isAddAppAsTabAvailable: boolean;
	isManageAvailable: boolean;
};

function convertToGlobalNavigationItem(
	item: GlobalNavigationItemModel,
	spaceNavigationHandler: ISpaceNavigationHandler,
	timezone: TimeZone
): GlobalNavigationItem {
	switch (item.itemData.kind) {
		case GlobalNavigationItemViewModelKind.Space:
			return new SpaceNavigationItem({
				id: item.id,
				name: item.name,
				model: item,
				spaceId: item.itemData
					.spaceId,
				spaceKind: item.itemData.spaceKind,
				userInfo: item.itemData.userInfo,
				spaceNavigationHandler
			});
		case GlobalNavigationItemViewModelKind.GenericApp:
			const {
				installedAppId,
				appType,
				conditionalAccess,
				maintenanceWindow
			} = item.itemData;

			return new AppNavigationItem(
				{
					id: item.id,
					name: item.name,
					model: item,
					installedAppId,
					appType,
					conditionalAccess,
					maintenanceWindow,
				},
				timezone
			);
		case GlobalNavigationItemViewModelKind.Group:
			return new GroupNavigationItem(
				item,
				spaceNavigationHandler,
				timezone
			);
		default:
			never(item.itemData);
	}
}

function convertToTreeItem(
	item: GlobalNavigationItem,
	mode: GlobalNavigationBarMode,
	actions: (readonly ItemAction[]) | undefined
): TreeViewItem<GlobalNavigationItem> {
	let content: string | ReactElement;
	if (item instanceof AppNavigationItem
		&& item.disabledStatus.isSet
	) {
		const disabledStatus: DisabledStatus = item.disabledStatus
			.value;

		content = (
			<>
				<span className={styles.disabledItemContent}>
					<span className={styles.disabledItemContentText}>
						{item.name}
					</span>

					<Icon
						value={{
							kind: "font",
							name: disabledStatus.icon,
							color: "danger"
						}}
						tooltip={disabledStatus.reason}
					/>
				</span>
			</>
		);
	} else {
		content = item.name;
	}

	return {
		value: item,
		content,
		title: item.name,
		isDisabled: !isGroupItem(item.model.itemData) && !item.canActivate(mode),
		children: item.children
			?.map(
				(x) => convertToTreeItem(
					x,
					mode,
					actions
				)
			),
		actions: isNotNil(actions)
			? {
				actions,
				context: item
			}
			: undefined
	};
}

type ItemActionContext = GlobalNavigationItem;
type ItemAction = Action<ItemActionContext>;

type TreeItemActionHandlers = {
	onEditSpace: (spaceId: number) => void;
	onDeleteSpace: (spaceId: number, spaceName: string) => void;
};

function listSelectionActions(
	handlers: TreeItemActionHandlers
): readonly ItemAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit-space",
			icon: "pen",
			title: localize("_Edit"),
			canExecute: (x) => x instanceof SpaceNavigationItem
				&& x.userInfo.canEdit,
			execute: (x) => {
				handlers.onEditSpace(
					(x as SpaceNavigationItem).spaceId
				);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "delete-space",
			icon: "trash-can",
			title: localize("_Delete"),
			canExecute: (x) => x instanceof SpaceNavigationItem
				&& x.userInfo.canDelete,
			execute: (x) => {
				handlers.onDeleteSpace(
					(x as SpaceNavigationItem).spaceId,
					x.name
				);
			}
		}
	];
}

function convertToCriteria(item: GlobalNavigationItem): GlobalNavigationCriteria | undefined {
	if (item instanceof SpaceNavigationItem) {
		return {
			type: "space",
			spaceId: item.spaceId
		};
	}

	if (item instanceof AppNavigationItem) {
		if (item.appType === AppType.Embed) {
			return {
				type: "embedApp",
				installedAppId: item.installedAppId
			};
		}

		return {
			type: "app",
			appType: item.appType
		};
	}

	if (item instanceof GroupNavigationItem) {
		return undefined;
	}

	throw new Error("Unknown navigation item");
}

function useGlobalNavigationActionsAvailability(
	permission: GlobalNavigationPermissionsModel
): GlobalNavigationActionsAvailability {
	return useMemo(
		() => {
			const {
				canChangeGlobalNavigationOrder,
				canManageGlobalNavigation,
				canManageSharedSpaces,
				canManageSpacePermissions
			} = permission;

			const isAvailableForUpsell: boolean = userConfiguration().isAdmin;
			const couldPotentiallyManageSharedSpaces: boolean = isAvailableForUpsell || canManageSharedSpaces;
			const couldPotentiallyChangeGlobalNavigationOrder: boolean = isAvailableForUpsell || canChangeGlobalNavigationOrder;
			const couldPotentiallyManageSpacePermissions: boolean = isAvailableForUpsell || canManageSpacePermissions;
			const couldPotentiallyManageGlobalNavigation: boolean = isAvailableForUpsell || canManageGlobalNavigation;

			const isCreateSharedSpaceAvailable: boolean = couldPotentiallyManageGlobalNavigation
				&& couldPotentiallyManageSharedSpaces;

			const isAddAppAsTabAvailable: boolean = couldPotentiallyManageGlobalNavigation;

			const isManageAvailable: boolean = couldPotentiallyManageGlobalNavigation
				|| couldPotentiallyManageSpacePermissions
				|| couldPotentiallyChangeGlobalNavigationOrder;

			return {
				isCreateSharedSpaceAvailable,
				isAddAppAsTabAvailable,
				isManageAvailable
			};
		},
		[permission]
	);
}

class GroupNavigationItem implements GlobalNavigationItem {
	public readonly id: number;
	public readonly name: string;
	public readonly model: GlobalNavigationItemModel;
	public readonly children: readonly GlobalNavigationItem[];

	constructor(
		options: GlobalNavigationItemModel,
		spaceNavigationHandler: ISpaceNavigationHandler,
		timezone: TimeZone
	) {
		this.id = options.id;
		this.name = options.name;
		this.model = options;

		if (!isGroupItem(options.itemData)) {
			throw new Error("Expected group item at this point.");
		}

		this.children = options.itemData
			.children
			.map(
				x => convertToGlobalNavigationItem(
					x,
					spaceNavigationHandler,
					timezone
				)
			);
	}

	public activate(): void {
		throw new Error("It should not be possible to activate the Group item.");
	}

	public canActivate(): boolean {
		return false;
	}

	public canDeactivate(): Promise<boolean> {
		return Promise.reject(new Error("It should not be possible to activate the Group item."));
	}
}

const createGroup: ActionGroup = {
	kind: "regular",
	name: "create-group",
	title: localize("_Create")
};

function getPathToItem(
	rootItems: TreeViewItem<GlobalNavigationItem>[],
	criteria: GlobalNavigationCriteria
): TreeViewItem<GlobalNavigationItem>[] | undefined {
	const findItem = (
		criteria: GlobalNavigationCriteria,
		current: TreeViewItem<GlobalNavigationItem>,
		walkedPath: TreeViewItem<GlobalNavigationItem>[]
	): TreeViewItem<GlobalNavigationItem>[] | undefined => {
		const currentCriteria: GlobalNavigationCriteria | undefined = convertToCriteria(current.value);

		if (deepComparison(currentCriteria, criteria)) {
			return [...walkedPath, current];
		}

		if (current.children !== "lazy" && isArray(current.children)) {
			walkedPath.push(current);
			for (const child of current.children) {
				const childResult: TreeViewItem<GlobalNavigationItem>[] | undefined = findItem(
					criteria,
					child,
					walkedPath
				);

				if (isNotNil(childResult)) {
					return childResult;
				}
			}

			walkedPath.pop();
		}

		return undefined;
	};

	let itemResult: TreeViewItem<GlobalNavigationItem>[] | undefined;

	for (const item of rootItems) {
		itemResult = findItem(
			criteria,
			item,
			[]
		);

		if (itemResult) {
			return itemResult;
		}
	}

	return undefined;
}
